import { useBreakpoints } from "@vueuse/core";
import { startOfDay, subMonths } from "date-fns";
import mixpanel from "mixpanel-browser";
import { defineStore, storeToRefs } from "pinia";
import { Platform } from "quasar";
import { computed, ref } from "vue";

import {
  identify as identifyAnalytics,
  updateIntercom,
} from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { initialize as initializeLogrocket } from "shared/boot/logrocket";
import { identify as identifyRollbar } from "shared/boot/rollbar";
import { BREAKPOINTS } from "shared/constants";
import { getCurrentTimestamp, parseDate } from "shared/helpers/date";
import { AnalyticsIntegration } from "shared/resources";
import appConfigService from "shared/services/appConfig";
import { type AppConfig } from "shared/services/appConfig/types";
import initChurnZero from "shared/services/churnzero";
import initPendo from "shared/services/pendo";
import { useAlertsStore } from "shared/stores/alerts";
import { useBookmarkStreamsStore } from "shared/stores/bookmarkStreams";
import { useFeatureTeasersStore } from "shared/stores/featureTeasers";
import { useMediaRequestsStore } from "shared/stores/mediaRequests";
import { useMentionCountsStore } from "shared/stores/mentionCounts";
import { useStreamGroupsStore } from "shared/stores/streamGroups";
import { useStreamsStore } from "shared/stores/streams";
import { useTranscriptRequestsStore } from "shared/stores/transcriptRequests";
import { useUserStore } from "shared/stores/user";
import { type Category, type Location } from "shared/types";
import { type Maybe, type Nullable } from "shared/types/utility";

const handleErrors = true;

export const useGlobalStore = defineStore("global", () => {
  const { greaterOrEqual, smaller } = useBreakpoints(BREAKPOINTS);
  const isAtLeastLgScreen = greaterOrEqual("lg");
  const isBelowSmScreen = smaller("sm");

  const analyticsIntegration = ref<Nullable<AnalyticsIntegration>>(null);

  const appConfig = ref<AppConfig>(
    appConfigService.getConfig(import.meta.env.VITE_REGION)
  );

  const categories = ref<Category[]>([]);
  const cities = ref<Location[]>([]);
  const disableMobileTrap = ref<boolean>(false);
  const globalDataError = ref<boolean>(false);
  const globalDataLoaded = ref<boolean>(false);
  const isTeamPickerOpen = ref<boolean>(false);

  const locations = ref<Location[]>([]);
  const isMenusOpen = ref<boolean>(false);
  const navbarCollapsed = ref<boolean>(false);
  const navbarLeftIcon = ref<Nullable<string>>(null);
  const navbarTitle = ref<string>("");
  const routerViewKey = ref<Maybe<string | number>>();
  const socialCategories = ref<Category[]>([]);
  const technicalServerError = ref<boolean>(false);
  const isHoveringMenu = ref<boolean>(false);

  const isTheMenuOpen = computed<boolean>(
    () =>
      (isAtLeastLgScreen.value && !navbarCollapsed.value) ||
      isHoveringMenu.value
  );

  const getContentDateCap = computed(() => appConfig.value?.contentDateCap);

  const isCisionOne = computed(
    () => appConfig.value.platformName === "CisionOne"
  );

  const isStreem = computed(() => appConfig.value.platformName === "Streem");

  function getLowestSearchableDate(currentDate: Date): Date {
    if (appConfig.value.contentDateCap.toString().length >= 10) {
      return parseDate(appConfig.value.contentDateCap);
    }

    return subMonths(startOfDay(currentDate), appConfig.value.contentDateCap);
  }

  function setGlobalDataLoading() {
    globalDataLoaded.value = false;
    globalDataError.value = false;
  }

  function setGlobalDataLoaded() {
    globalDataLoaded.value = true;
    globalDataError.value = false;
  }

  function disableMobileTrapPage() {
    disableMobileTrap.value = true;
  }

  function setAppConfig(config: AppConfig) {
    appConfig.value = config;
  }

  function setIsTeamPickerOpen(value: boolean) {
    isTeamPickerOpen.value = value;
  }

  async function getAppData() {
    const { fetchAlertCount } = useAlertsStore();
    const { fetchFeatureTeasers } = useFeatureTeasersStore();

    const userStore = useUserStore();

    const { accountManager, currentUser, isAdminUser } = storeToRefs(userStore);

    // Initialize LogRocket as soon as possible
    const LogRocket = initializeLogrocket({
      isAdminUser: isAdminUser.value,
    });

    updateIntercom({ hide_default_launcher: true });

    await userStore.getUser();

    setGlobalDataLoading();

    const result = await Promise.all([
      useStreamGroupsStore().getStreamGroups(),
      getLocations(),
      getCategories(),
      useStreamsStore().getStreams(),
    ]);

    await Promise.all([
      useUserStore().getOrganisation(),
      useUserStore().getTeam(),
    ]);

    useGlobalStore().setGlobalDataLoaded();

    setTimeout(() => {
      useMentionCountsStore().fetchMentionCounts({ first_load: true });
      useBookmarkStreamsStore().fetchBookmarkCounts();
      fetchAlertCount();
      useMediaRequestsStore().fetchNewMediaRequestsCount();
      useTranscriptRequestsStore().fetchTranscriptRequestCount();
      fetchFeatureTeasers();
      fetchAnalyticsIntegration();
      useUserStore().getMultiplier();
    }, 500);

    const organisationRegion = currentUser.value.organisation.region;

    if (!["au", "nz"].includes(organisationRegion)) {
      const config = appConfigService.getConfig(organisationRegion);

      setAppConfig(config);
    }

    if (!isAdminUser.value) {
      useUserStore().updateUser({ last_accessed_at: getCurrentTimestamp() });
    }

    const userId = isAdminUser.value
      ? `AM-${currentUser.value.id}`
      : currentUser.value.id;

    const displayName =
      isAdminUser.value && accountManager.value
        ? `${accountManager.value.name} as ${currentUser.value.first_name} ${currentUser.value.last_name}`
        : `${currentUser.value.first_name} ${currentUser.value.last_name}`;

    identifyRollbar({
      email: currentUser.value.email,
      id: userId,
      username: displayName,
    });

    // identify with logrocket only when a admin user to track sessions
    const identityFn =
      isAdminUser.value && LogRocket
        ? (id: string, opts: any) => LogRocket.identify(id, opts)
        : identifyAnalytics;

    const identityAttributes = {
      auto_refresh_enabled: currentUser.value.auto_refresh_streams,
      company: { id: userStore.organisation.id },
      contract_services: userStore.organisation.contract_services ?? [],
      email: currentUser.value.email,
      first_name: currentUser.value.first_name,
      global_customer_id: userStore.organisation.global_customer_id || "",
      id: currentUser.value.id,
      last_name: currentUser.value.last_name,
      name: `${currentUser.value.first_name} ${currentUser.value.last_name}`,
      organisation_id: userStore.organisation.id,
      organisation_name: userStore.organisation.name,
      outreach_role: currentUser.value.outreach_role,
      phone: currentUser.value.international_phone_number,
      role: currentUser.value.role,
      show_intercom: currentUser.value.show_intercom,
      social_role: currentUser.value.social_role,
      ui_language: currentUser.value.language,
      user_created_at: currentUser.value.created_at,
      user_hash: currentUser.value.user_hash,
      ...(Platform.is.mobile && {
        last_platform: "Mobile",
        mobile_app_version: "__version_number__",
      }),
      ...(Platform.is.desktop && {
        desktop_app_version: "__version_number__",
        last_platform: "Desktop",
      }),
      ...(Platform.is.ios && { mobile_type: "iOS" }),
      ...(Platform.is.android && { mobile_type: "Android" }),
    };

    identityFn(String(currentUser.value.id), identityAttributes);

    if (!isAdminUser.value) {
      initChurnZero(identityAttributes);
      initPendo(identityAttributes);
    }

    if (!isAdminUser.value && import.meta.env.VITE_MIXPANEL_TOKEN) {
      mixpanel.set_group("organisation_id", currentUser.value.organisation.id);
    }

    return result;
  }

  async function getLocations() {
    const response = await $streemApiV1.get<Location[]>("locations", {
      handleErrors,
      params: {
        visible: true,
      },
    });

    setLocations(response.data);
  }

  function setLocations(newLocations: Location[]) {
    locations.value = newLocations;
  }

  async function getCities() {
    const response = await $streemApiV1.get<Location[]>("locations", {
      handleErrors,
      params: {
        type: "City",
      },
    });

    cities.value = response.data;
  }

  async function getCategories() {
    const response = await $streemApiV1.get<Category[]>("categories", {
      handleErrors,
      params: {
        include_social: true,
      },
    });

    setCategories(response.data);
  }

  function setCategories(data: Category[]) {
    categories.value = data.filter(({ social }) => !social);
    socialCategories.value = data.filter(({ social }) => social);
  }

  async function fetchAnalyticsIntegration() {
    const userStore = useUserStore();
    const { currentUser } = storeToRefs(userStore);

    try {
      const response = await AnalyticsIntegration.includes([
        "analyticsDataStreams",
      ])
        .where({
          organisationId: currentUser.value.organisation.id,
        })
        .selectExtra(["isIntegrated"])
        .first();

      analyticsIntegration.value = response.data;
    } catch {
      // do nothing
    }
  }

  async function turnOffUserStreamsNotifications() {
    await $streemApiV1.put("streams/snooze_notifications", { handleErrors });

    useStreamsStore().getStreams();
  }

  function setNavbarTitle(title: string) {
    navbarTitle.value = title;
  }

  function setNavbarLeftIcon(icon: Nullable<string>) {
    navbarLeftIcon.value = icon;
  }

  function setRouterViewKey(key: Maybe<string | number>) {
    routerViewKey.value = key;
  }

  function toggleNavigationMenu() {
    if (isBelowSmScreen.value) {
      isMenusOpen.value = !isMenusOpen.value;
    } else {
      navbarCollapsed.value = !navbarCollapsed.value;
    }
  }

  return {
    analyticsIntegration,
    appConfig,
    categories,
    cities,
    disableMobileTrap,
    disableMobileTrapPage,
    fetchAnalyticsIntegration,
    getAppData,
    getCategories,
    getCities,
    getContentDateCap,
    getLocations,
    getLowestSearchableDate,
    globalDataError,
    globalDataLoaded,
    isCisionOne,
    isHoveringMenu,
    isMenusOpen,
    isStreem,
    isTeamPickerOpen,
    isTheMenuOpen,

    locations,
    navbarCollapsed,
    navbarLeftIcon,
    navbarTitle,
    routerViewKey,
    setAppConfig,
    setCategories,
    setGlobalDataLoaded,
    setGlobalDataLoading,
    setIsTeamPickerOpen,
    setLocations,
    setNavbarLeftIcon,
    setNavbarTitle,
    setRouterViewKey,
    socialCategories,
    technicalServerError,
    toggleNavigationMenu,
    turnOffUserStreamsNotifications,
  };
});

export default useGlobalStore;
